import React, { useEffect, useState } from "react";
import Icons from "../../../Assets/Icons";
import "./specimenLogistics.css";
import Widgets from "../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../utils/strings";
import { useDispatch, useSelector } from "react-redux";
import { getSpecimenLogs } from "../../../modules/NursignCare/ManageRequest/manageRequestThunk";

const LIMIT = 10;

const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const SpecimenLogisticsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const breadcrumbs = ["nursingCare", "specimenLogistics"];
  const paths = [strings.navigation.nursingCareManageService, strings.navigation.specimenLogistics];

  const { specimenLogs, specimenTotalPages, specimenCurrentPage } = useSelector((state) => state.manageRequest);
  const [page, setPage] = useState(1);
  const totalPages = specimenTotalPages || 1;
  const currentPage = specimenCurrentPage || page;

  useEffect(() => {
    dispatch(getSpecimenLogs({ page, limit: LIMIT }));
  }, [page]);

  const handleExport = () => {
    const headers = ["Patient Name", "Phone", "Sample Name", "Homecare Staff", "Collected Date Time", "Received Date Time", "Status"];
    const rows = specimenLogs.map((s) => {
      const patientName = `${s.relativeDetails?.firstName || ""} ${s.relativeDetails?.lastName || ""}`.trim();
      const phone = s.userDetails ? `+${s.userDetails.countryCode} ${s.userDetails.phoneNumber}` : "-";
      const nurseName = s.nurseDetails ? `${s.nurseDetails.firstName?.en || s.nurseDetails.firstName || ""} ${s.nurseDetails.lastName?.en || s.nurseDetails.lastName || ""}`.trim() : "-";
      return [patientName, phone, s.sampleName || "-", nurseName, formatDateTime(s.collectionTime), formatDateTime(s.receivedDate), s.status || "-"];
    });
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `specimen_logs_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, { type: "ellipsis" }, totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      } else {
        pages.push(1, { type: "ellipsis" }, currentPage, { type: "ellipsis" }, totalPages);
      }
    }

    return (
      <div className="department-pagination-list">
        <button className="department-pagination-prev-list" onClick={() => handlePage(currentPage - 1)} disabled={currentPage === 1}>
          <img src={Icons.leftArrow} alt="<" />
        </button>
        {pages.map((p, i) => (
          <button
            key={i}
            onClick={() => typeof p === "number" && handlePage(p)}
            disabled={p === currentPage || p?.type === "ellipsis"}
            className={`department-pagination-btn-list ${p === currentPage ? "active-list" : ""} ${p?.type === "ellipsis" ? "ellipse" : ""}`}
          >
            {typeof p === "number" ? p : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
          </button>
        ))}
        <button className="department-pagination-next-list" onClick={() => handlePage(currentPage + 1)} disabled={currentPage === totalPages}>
          <img src={Icons.rightArrow} alt=">" />
        </button>
      </div>
    );
  };

  return (
    <div className="mr-container specimen-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
      <div className="manage-service-heading specimen-heading">{t("specimenLogs")}</div>

      <div className="specimen-actions">
        <button className="specimen-btn specimen-export-btn" onClick={handleExport}>
          <img src={Icons.exportSpecimen} alt="" className="specimen-btn-icon" />
          <span>{t("export")}</span>
        </button>
      </div>

      <div className="specimen-list-box">
        <table className="specimen-table">
          <thead>
            <tr>
              <th>{t("patientDetails")}</th>
              <th>{t("sampleName")}</th>
              <th>{t("homeCareStaff")}</th>
              <th>{t("collectedDateTime")}</th>
              <th>{t("receivedDateTime")}</th>
              <th>{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {specimenLogs.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "#9ca3af", padding: "2.4rem" }}>
                  {t("noRecordFound")}
                </td>
              </tr>
            ) : (
              specimenLogs.map((s) => {
                const patientName = `${s.relativeDetails?.firstName || ""} ${s.relativeDetails?.lastName || ""}`.trim();
                const phone = s.userDetails ? `+${s.userDetails.countryCode} ${s.userDetails.phoneNumber}` : "-";
                const nurseName = s.nurseDetails ? `${s.nurseDetails.firstName?.en || s.nurseDetails.firstName || ""} ${s.nurseDetails.lastName?.en || s.nurseDetails.lastName || ""}`.trim() : "-";

                return (
                  <tr key={s._id}>
                    <td>
                      <div className="patient-cell">
                        <div className="patient-info">
                          <div className="patient-name">{patientName}</div>
                          <div className="patient-sub">{phone}</div>
                        </div>
                      </div>
                    </td>
                    <td>{s.sampleName}</td>
                    <td>{nurseName}</td>
                    <td>{formatDateTime(s.collectionTime)}</td>
                    <td>{formatDateTime(s.receivedDate)}</td>
                    <td>{s.status === "SUBMITTED" ? <span className="status-pill status-submitted">{t("submitted")}</span> : <span className="status-pill status-pending">{t("pending")}</span>}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && renderPagination()}
    </div>
  );
};

export default SpecimenLogisticsList;
